import { useCallback, useRef, useState } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

export interface ElementDrop {
  itemId: string;
  parentId?: string;
  panelId?: string;
  index: number;
  row: number;
  column: number;
}

interface Options {
  workspaceId: string;
  canvasId: string | null;
  revision?: number;
  dirty: boolean;
  refresh: () => Promise<unknown>;
  onDraftMove: (ownerId: string, panelId: string, index: number) => void;
  onError: (message: string) => void;
}

/** Persist one panel move; never replace the canvas or discard an unsaved draft. */
export function useCanvasTabMove(options: Options) {
  const authFetch = useAuthFetch();
  const [moving, setMoving] = useState(false);
  const inFlight = useRef(false);
  const latest = useRef(options);
  latest.current = options;
  const move = useCallback(
    async (
      ownerId: string,
      panelId: string,
      index: number,
      elementDrop?: ElementDrop,
    ) => {
      if (inFlight.current) return;
      const itemKind = elementDrop ? "elements" : "tabs";
      const savedSubject = elementDrop
        ? "The element position"
        : "The tab order";
      if (!options.canvasId) {
        options.onDraftMove(ownerId, panelId, index);
        return;
      }
      if (options.dirty) {
        options.onError(`Save changes before reordering ${itemKind}.`);
        return;
      }
      if (!Number.isInteger(options.revision) || !options.revision) {
        options.onError(`Reload the canvas before reordering ${itemKind}.`);
        return;
      }
      inFlight.current = true;
      setMoving(true);
      try {
        await authFetch(
          `/api/workspace/myvault/canvases/${options.canvasId}/items/move`,
          "move canvas item",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-workspace-id": options.workspaceId,
            },
            body: JSON.stringify({
              collection: elementDrop ? "elements" : "panels",
              owner_element_id: elementDrop ? null : ownerId,
              item_id: panelId,
              source_index: null,
              index,
              base_revision: options.revision,
              collection_key: null,
              new_parent_id: elementDrop?.parentId || null,
              new_panel_id: elementDrop?.panelId || null,
              grid_row: elementDrop?.row ?? null,
              grid_column: elementDrop?.column ?? null,
            }),
          },
        );
        if (latest.current.dirty) {
          options.onError(
            `${savedSubject} was saved. Your unsaved edits were preserved; reload and reconcile before saving.`,
          );
          return;
        }
        const refreshed = await options.refresh();
        if (!refreshed)
          options.onError(
            `${savedSubject} was saved, but the canvas could not be reloaded. Reload before editing.`,
          );
      } catch (error) {
        if (
          (error as { status?: number }).status === 409 &&
          !latest.current.dirty
        ) {
          await options.refresh();
        }
        options.onError(
          error instanceof Error
            ? error.message
            : `Could not reorder ${itemKind}.`,
        );
      } finally {
        inFlight.current = false;
        setMoving(false);
      }
    },
    [authFetch, options],
  );
  return {
    move,
    moving,
    moveElement: (drop: ElementDrop) => move("", drop.itemId, drop.index, drop),
  };
}
